// @flow

import { v4 } from 'uuid';
import get from 'lodash/get';
import find from 'lodash/find';
import React, { Fragment, useEffect, useState, useRef, useMemo } from 'react';
import type { Node } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useIntl, FormattedMessage } from 'react-intl';
import Lazyload from 'react-lazyload';
import loadable from '@loadable/component';
import { timeout } from 'promise-timeout';
import { Row, Col } from '@riseart/antd-provider';
import { Section, SectionContent, Wrapper, VerticalContainer } from '@riseart/layout';
import {
  CaptionFigure,
  LazyloadPlaceholder,
  ScrollMarkerProvider,
  ScrollMarkerMap,
  ScrollMarkerAnchor,
  MediaQuery,
  Banner as CommonBanner,
  VerticalMenu,
} from '@riseart/common';
import { ArtistComment } from '@riseart/artist';
import {
  artist as ENUM_ARTIST,
  HTTP_STATUS,
  filter as ENUM_FILTER,
  meta as ENUM_META,
  pagination as ENUM_PAGINATION,
} from 'Enum';
import { Query } from 'shared_services/apollo/Query';
import { META_SUBSCRIBER_NAME } from 'shared_services/riseart/meta/Meta';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import {
  NOTFOUND_STATE_KEYS,
  notFoundUpdate,
} from 'shared_services/redux/actions/application/notFound';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { localeSelector } from 'shared_services/redux/selectors/locale';
import { selectNotFound } from 'shared_services/redux/selectors/notFound';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { MENU_ITEMS, mapArtistPageData } from 'shared_services/riseart/utils/artist';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { parseHash } from 'shared_services/riseart/utils/RouteUtils';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { gtmArtistView } from 'shared_data/providers/google/tagmanager/Actions';
import DETAIL_ARTIST from 'shared_data/queries/artist/detail.graphql';
import DETAIL_ARTIST_ADVANCED from 'shared_data/queries/artist/detailAdvanced.graphql';
import FILTER_ARTIST_QUOTES_AND_THOUGHTS_QUERY from 'shared_data/queries/artist/filterQuotesAndCuratorThoughts.graphql';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { ArtistBio } from 'shared_components/artist/bio/Bio';
import { VerticalMenuOverlay } from 'shared_components/common/menu/overlay/Menu';
import { ArtistDetailMeta } from 'shared_components/artist/detail/Meta';
import { Picture, PicturePlaceholder } from 'shared_components/common/artdirection/picture/Picture';
import { NotFoundArtist } from 'shared_components/common/notfound/Artist';
import { FilterArtistLinks } from 'shared_components/common/filters/links/Artists';
import { FilterCategoriesLinks } from 'shared_components/common/filters/links/Categories';
import { ArtistArtGrid } from 'shared_components/artist/art/gallery/Grid';
import { ArtistArtList } from 'shared_components/artist/art/gallery/List';
import { ArtistArtGallery } from 'shared_components/artist/art/gallery/Gallery';
import { ArtGallerySwitcher } from 'shared_components/arts/gallery/Switcher';
import { useGallerySwitch } from 'shared_components/arts/gallery/useGallerySwitch';

import {
  raScreenSm,
  raScreenLg,
  raVisibilityHidden,
} from '@riseart/antd-provider/dist/website/variables.less';
import {
  artistSecondaryBannerCls,
  artistProfileNewsWrapperCls,
  artistProfileStudioWrapperCls,
  artistProfileArtSectionCls,
} from 'shared_components/pages/artist/Profile.less';
import { menuVerticalMargin, artistBioSectionCls } from 'shared_components/artist/bio/Bio.less';

const PRELOADER_SUBSCRIBER_IDS = {
  readArtist: v4(),
  filterEditorials: v4(),
  filterFeed: v4(),
  news: v4(),
};

const LAZYLOAD_CONFIG = {
  once: true,
  height: 600,
  offset: 200,
  placeholder: <LazyloadPlaceholder height={600} />,
};

/**
 * lazyloadSections
 *
 * @param {Object} artistData
 * @param {number} timeoutMs
 * @returns
 */
function lazyloadSections(artistData: Object, timeoutMs: number = 10000) {
  const components = {
    commission: null,
    feed: null,
    news: null,
    browseArt: null,
  };

  if (!artistData) {
    return components;
  }

  // Load ArtistCommissionSection
  if (artistData.commissions) {
    components.commission = loadable(
      () => timeout(import('shared_components/artist/sections/Commission'), timeoutMs),
      { resolveComponent: (components) => components.ArtistCommissionSection },
    );
  } else {
    // Load ArtistBrowseArtSection
    components.browseArt = loadable(
      () => timeout(import('shared_components/artist/sections/BrowseArt'), timeoutMs),
      { resolveComponent: (components) => components.ArtistBrowseArtSection },
    );
  }

  // Load ArtistFeedSection
  if (!artistData.feed) {
    components.feed = loadable(
      () => timeout(import('shared_components/artist/sections/Feed'), timeoutMs),
      { resolveComponent: (components) => components.ArtistFeedSection },
    );
  }

  // Load ArtistNewsSection
  if (!artistData.news) {
    components.news = loadable(
      () => timeout(import('shared_components/artist/sections/News'), timeoutMs),
      { resolveComponent: (components) => components.ArtistNewsSection },
    );
  }

  return components;
}

type Props = {
  staticContext: ?Object,
  routeConfig: Object,
  isLargeScreen: boolean,
};

/**
 * ArtistProfileComponent
 */
const ArtistProfileComponent = ({ staticContext, routeConfig, isLargeScreen }: Props) => {
  const { id: artistId } = useParams();
  const history = useHistory();
  const match = useRouteMatch();
  const { translatedLocation: location } = useUrlParams();
  const currentLocale = useSelector(localeSelector, shallowEqual);
  const dispatch: Function = useDispatch();
  const { formatMessage } = useIntl();
  const notFoundArtist = useSelector(selectNotFound(NOTFOUND_STATE_KEYS.ARTIST));
  const [firstImageItem, setFirstImageItem] = useState<Object | null | void>();
  const artistViewId = useRef();
  const [selectedArtId, setSelectedArtId] = useState();
  const selectedArtDetailsId = useMemo(
    () => (parseHash(location.hash) || {}).detail,
    [location.hash],
  );
  const generateGallerySwitcherUrls = useGallerySwitch({
    search: location.search,
    isGalleryMode: !!selectedArtDetailsId,
  });
  const { loading, data, error } = useQuery(
    isLargeScreen ? DETAIL_ARTIST_ADVANCED : DETAIL_ARTIST,
    {
      variables: isLargeScreen
        ? {
            id: artistId,
            artistId,
            type: ENUM_ARTIST.editorials.TYPE_ARTIST_REASON,
            page: 1,
            items: 3,
          }
        : { id: artistId },
      context: {
        customOptions: {
          errorSuppressFromResponse: true,
          errorHandler: ({ graphQLErrors = [] }) => {
            const found404Error = find(
              graphQLErrors,
              (err) => err && err.errorInfo && err.errorInfo.status === HTTP_STATUS.NOT_FOUND,
            );

            if (found404Error) {
              const artistInfo = get(found404Error, 'errorInfo.additional.artist');

              if (artistInfo) {
                dispatch(notFoundUpdate(NOTFOUND_STATE_KEYS.ARTIST, artistInfo));

                return { shouldOverwriteDefaultHandler: true };
              }

              if (staticContext) staticContext.status = HTTP_STATUS.MOVED_PERMANENTLY;

              history.replace(
                UrlAssembler.artistList({
                  search: {
                    [LocalizedConfig.get('navigation.uri.query.params.message')]:
                      HTTP_STATUS.NOT_FOUND,
                  },
                }),
              );

              return { shouldOverwriteDefaultHandler: true };
            }

            return { shouldOverwriteDefaultHandler: false };
          },
        },
      },
    },
  );
  const { detailArtist: artistData, artistEditorials } = data || {};

  useEffect(() => {
    setSelectedArtId(selectedArtDetailsId);
  }, [selectedArtDetailsId]);

  // Clean firstItemImage of artist changes
  useEffect(() => {
    setFirstImageItem();
  }, [artistData]);

  // Dispatch a GTM event if artist changes and firstImageItem is loaded or it is not existing
  useEffect(() => {
    if (artistData && artistViewId.current !== artistData.id && firstImageItem !== undefined) {
      artistViewId.current = artistData.id;
      dispatch(gtmArtistView({ ...artistData, primaryArt: firstImageItem }));
    }
  }, [dispatch, artistData, firstImageItem]);

  const {
    hasError,
    canonicalURL,
    subpageCanonicalRedirect,
    hrefLangRedirects,
    artistUrl,
    name,
    artistMenuItems,
    artistImages: { mainBannerImage, additionalBannerImage, detailsImage },
  } = useMemo(
    () =>
      mapArtistPageData(artistData, error, MENU_ITEMS.profile, 'artistProfile', {
        formatMessage,
        locale: currentLocale,
        location,
      }),
    [artistData, error, formatMessage, currentLocale, location],
  );
  const {
    commission: ArtistCommissionSectionComponent,
    feed: ArtistFeedSectionComponent,
    news: ArtistNewsSectionComponent,
    browseArt: ArtistBrowseArtSectionComponent,
  } = useMemo(() => lazyloadSections(artistData), [artistData]);
  const hasCV = useMemo(
    () =>
      artistData &&
      (artistData.hasDegrees ||
        artistData.hasAwards ||
        artistData.hasExhibitions ||
        artistData.hasCollections ||
        artistData.hasBibliography),
    [artistData],
  );
  const hasNews = useMemo(
    () => artistData && (artistData.hasEvents || artistData.hasArticles),
    [artistData],
  );

  if (
    !artistData &&
    notFoundArtist &&
    artistId &&
    parseInt(notFoundArtist.id, 10) === parseInt(artistId, 10)
  ) {
    return (
      <LayoutDefault scrollTopOnPathChange>
        <NotFoundArtist data={notFoundArtist} metaSubscriber={META_SUBSCRIBER_NAME.ARTIST_META} />
      </LayoutDefault>
    );
  }

  return (
    <LayoutDefault scrollTopOnPathChange>
      <DataProviderPreloaderPage uniqueId={PRELOADER_SUBSCRIBER_IDS.readArtist} attach={loading} />
      {loading && <IsomorphicRipple isActive isFullScreen />}
      <ArtistArtList
        ssr
        artistId={artistId}
        location={location}
        itemsPerPage={isLargeScreen ? 16 : 10}
        onCompleted={(artistListQueryData) =>
          setFirstImageItem(get(artistListQueryData, 'filterArtistArt.items[0]', null))
        }
        onError={() => setFirstImageItem(null)}
      >
        {({ items, pagination, loading: artLoading }) => {
          if (!artistData) {
            return null;
          }

          const firstItem = items && items[0] ? items[0] : null;
          const viewSwitcher = firstItem ? (
            <ArtGallerySwitcher
              {...generateGallerySwitcherUrls({
                detailId: firstItem.id,
                id: artistData.id,
                alias: artistData.alias,
                artistSubpageKey: 'artistProfile',
              })}
            />
          ) : null;

          return (
            <Fragment>
              {/* Meta Provider */}
              <ArtistDetailMeta
                hasError={hasError}
                artistData={artistData}
                mainBannerImage={mainBannerImage}
                canonicalURL={canonicalURL}
                subpageCanonicalRedirect={subpageCanonicalRedirect}
                hrefLangRedirects={hrefLangRedirects}
                formatMessage={formatMessage}
                translationKeyPrefix="meta.artistProfile"
                additionalMetas={{
                  [ENUM_META.METATYPE.SCRIPT_LD_JSON_AGGREGATE_RATING]: generateLDJSON(
                    LDTYPES.AGGREGATE_RATING,
                    artistData.name,
                  ),
                }}
              />
              <ArtistArtGallery
                viewSwitcher={viewSwitcher}
                items={items}
                currentLocale={currentLocale}
                pagination={pagination}
                loading={artLoading}
                artistUrl={artistUrl}
                name={name}
                artistMenuItems={artistMenuItems}
                match={match}
                location={location}
                history={history}
                selectedArtDetailsId={selectedArtDetailsId}
                paginationType={ENUM_PAGINATION.type.SLIDE}
                domain={ENUM_FILTER.domain[routeConfig.filterDomainKey]}
                urlParams={{
                  id: artistData.id,
                  alias: artistData.alias,
                  role: artistData.role,
                  url: UrlAssembler.artistSubpage('artistArt', artistData.id, artistData.alias),
                }}
              />
              <div className={selectedArtId ? raVisibilityHidden : null}>
                <ScrollMarkerProvider>
                  {/* Artist main details/bio section */}
                  <Section className={artistBioSectionCls}>
                    {isLargeScreen ? <ScrollMarkerAnchor id={MENU_ITEMS.profile} /> : null}
                    <Wrapper>
                      <ArtistBio
                        data={artistData}
                        image={mainBannerImage}
                        showCv={hasCV}
                        url={canonicalURL}
                        editorials={(artistEditorials && artistEditorials.items) || []}
                      >
                        <MediaQuery minWidth={raScreenLg}>
                          {(isScreenLg) =>
                            isScreenLg ? (
                              <ScrollMarkerMap>
                                {({ lastActiveId }) => (
                                  <VerticalMenu
                                    items={artistMenuItems}
                                    correction={parseInt(menuVerticalMargin, 10)}
                                    selectedFixedItem={lastActiveId}
                                  />
                                )}
                              </ScrollMarkerMap>
                            ) : (
                              <VerticalMenuOverlay title={name} items={artistMenuItems} />
                            )
                          }
                        </MediaQuery>
                      </ArtistBio>
                      {isLargeScreen && hasCV ? <ScrollMarkerAnchor id={MENU_ITEMS.cv} /> : null}
                    </Wrapper>
                  </Section>
                  {/* Artist Art */}
                  <Section className={artistProfileArtSectionCls}>
                    <ArtistArtGrid
                      viewSwitcher={viewSwitcher}
                      items={items}
                      currentLocale={currentLocale}
                      pagination={pagination}
                      loading={artLoading}
                      artistUrl={artistUrl}
                      name={name}
                      artistMenuItems={artistMenuItems}
                      artistData={artistData}
                      match={match}
                      location={location}
                      artistSubpageKey="artistProfile"
                      history={history}
                      selectedArtDetailsId={selectedArtDetailsId}
                      paginationType={ENUM_PAGINATION.type.SLIDE}
                      domain={ENUM_FILTER.domain[routeConfig.filterDomainKey]}
                      urlParams={{
                        id: artistData.id,
                        alias: artistData.alias,
                        role: artistData.role,
                        url: UrlAssembler.artistSubpage(
                          'artistArt',
                          artistData.id,
                          artistData.alias,
                        ),
                      }}
                    />
                  </Section>
                  {/* Additional Banner */}
                  {additionalBannerImage ? (
                    <Section>
                      <Wrapper sizes="fullWidth" noPadding className={artistSecondaryBannerCls}>
                        <CaptionFigure caption={additionalBannerImage.caption}>
                          <CommonBanner
                            size="large"
                            media={
                              <Picture
                                type="artist.profileSecondaryBanner"
                                artDirectionKey="artist.additional"
                                image={additionalBannerImage}
                                alt={additionalBannerImage.caption || name}
                                lazyload={LAZYLOAD_CONFIG}
                              />
                            }
                          />
                        </CaptionFigure>
                      </Wrapper>
                    </Section>
                  ) : null}
                  {/* Artist/Curator comments */}
                  <Query
                    query={FILTER_ARTIST_QUOTES_AND_THOUGHTS_QUERY}
                    variables={{
                      artistId: artistData.id,
                      artistQuotesType: ENUM_ARTIST.editorials.TYPE_ARTIST_QUOTE,
                      curatorThoughtsType: ENUM_ARTIST.editorials.TYPE_CURATOR_THOUGHT,
                      page: 1,
                      items: 1,
                    }}
                    ssr={false}
                  >
                    {({ data }) => {
                      let [mainComment] =
                        (data && data.artistQuotes && data.artistQuotes.items) || [];
                      let [secondaryComment] =
                        (data && data.curatorThoughts && data.curatorThoughts.items) || [];

                      if (!mainComment && !secondaryComment) {
                        return null;
                      }

                      // If no secondaryComment and has a detailsImage,
                      // then the mainComment is placed next to the detailsImage
                      if (mainComment && !secondaryComment && detailsImage) {
                        secondaryComment = { ...mainComment };
                        mainComment = null;
                      }

                      return (
                        <React.Fragment>
                          {mainComment ? (
                            <Section>
                              <Wrapper
                                sizes={{
                                  xs: 'wide',
                                  sm: 'wide',
                                  md: 'wide',
                                  lg: 'narrow',
                                  xl: 'narrow',
                                }}
                              >
                                <ArtistComment
                                  includeQuotes
                                  showAuthorDash
                                  author={
                                    mainComment.authorName ||
                                    (mainComment.author && mainComment.author.fullName)
                                  }
                                >
                                  {mainComment.text}
                                </ArtistComment>
                              </Wrapper>
                            </Section>
                          ) : null}
                          {secondaryComment && detailsImage ? (
                            <Section>
                              <Wrapper
                                sizes={{
                                  xs: 'wide',
                                  sm: 'wide',
                                  md: 'wide',
                                  lg: 'narrow',
                                  xl: 'narrow',
                                }}
                              >
                                <Row type="flex" justify="center" gutter={90}>
                                  <Col xs={24} sm={10} order={isLargeScreen ? 0 : 1}>
                                    <VerticalContainer>
                                      <ArtistComment
                                        size="small"
                                        includeQuotes
                                        showAuthorDash
                                        author={
                                          secondaryComment.authorName ||
                                          (secondaryComment.author &&
                                            secondaryComment.author.fullName)
                                        }
                                      >
                                        {secondaryComment.text}
                                      </ArtistComment>
                                    </VerticalContainer>
                                  </Col>
                                  <Col xs={24} sm={9} order={isLargeScreen ? 1 : 0}>
                                    <VerticalContainer>
                                      <CaptionFigure caption={detailsImage.caption}>
                                        <PicturePlaceholder
                                          type="artist.profileCommentImage"
                                          artDirectionKey="artist.detail"
                                          image={detailsImage}
                                          alt={detailsImage.caption || name}
                                          lazyload={LAZYLOAD_CONFIG}
                                        />
                                      </CaptionFigure>
                                    </VerticalContainer>
                                  </Col>
                                </Row>
                              </Wrapper>
                            </Section>
                          ) : null}
                        </React.Fragment>
                      );
                    }}
                  </Query>
                  {/* Artist Commissions */}
                  {ArtistCommissionSectionComponent ? (
                    <ArtistCommissionSectionComponent
                      title={
                        <FormattedMessage id="components.artist.commissionTitle" values={{ name }}>
                          {(title: string) => title}
                        </FormattedMessage>
                      }
                      subtitle={
                        <FormattedMessage id="components.artist.commissionSubtitle">
                          {(subtitle: string) => subtitle}
                        </FormattedMessage>
                      }
                      background={artistData.hasFeed ? null : 'gray'}
                      data={artistData}
                      canonicalURL={canonicalURL}
                    />
                  ) : null}
                  {/* In the Studio */}
                  {ArtistFeedSectionComponent ? (
                    <Lazyload offset={500}>
                      <ArtistFeedSectionComponent
                        artistName={name}
                        data={artistData}
                        background={
                          hasNews || (!hasNews && !artistData.commissions) ? 'gray' : null
                        }
                        title={
                          <FormattedMessage
                            id="components.artist.artistStudio"
                            values={{ name: artistData.name }}
                          >
                            {(title: string) => title}
                          </FormattedMessage>
                        }
                        lazyloadConfig={LAZYLOAD_CONFIG}
                        scrollMarker={
                          isLargeScreen ? <ScrollMarkerAnchor id={MENU_ITEMS.studio} /> : null
                        }
                        className={artistProfileStudioWrapperCls}
                        preloaderId={PRELOADER_SUBSCRIBER_IDS.filterFeed}
                      />
                    </Lazyload>
                  ) : null}
                  {/* News */}
                  {ArtistNewsSectionComponent ? (
                    <Lazyload offset={500}>
                      <ArtistNewsSectionComponent
                        data={artistData}
                        background={!artistData.hasFeed && !artistData.commissions ? 'gray' : null}
                        title={
                          <FormattedMessage
                            id="components.artist.artistNews"
                            values={{ name: artistData.name }}
                          >
                            {(title: string) => title}
                          </FormattedMessage>
                        }
                        scrollMarker={
                          isLargeScreen ? <ScrollMarkerAnchor id={MENU_ITEMS.news} /> : null
                        }
                        className={artistProfileNewsWrapperCls}
                        preloaderId={PRELOADER_SUBSCRIBER_IDS.news}
                      />
                    </Lazyload>
                  ) : null}
                  {/* 'Not what you were looking for' section */}
                  {ArtistBrowseArtSectionComponent ? (
                    <ArtistBrowseArtSectionComponent
                      background={
                        !artistData.hasFeed || (artistData.hasFeed && hasNews) ? 'gray' : null
                      }
                      hideBorder={
                        (artistData.hasFeed && !hasNews) || (!artistData.hasFeed && hasNews)
                      }
                    />
                  ) : null}
                  <Section>
                    <Wrapper>
                      <SectionContent
                        title={formatMessage({
                          id: 'components.art.similarArtistLinks',
                        })}
                        headingProps={{ tag: 'span' }}
                        headingSubTitleProps={{ tag: 'span' }}
                      >
                        <FilterArtistLinks
                          domain={ENUM_FILTER.domain.ARTIST}
                          medium={get(artistData, 'mediums[0]')}
                          style={get(artistData, 'styles[0]')}
                          ignoreArtistIds={artistData.id}
                        />
                      </SectionContent>
                    </Wrapper>
                  </Section>
                  <Section>
                    <Wrapper>
                      <SectionContent
                        title={formatMessage({
                          id: 'components.art.similarCategoryLinks',
                        })}
                        headingProps={{ tag: 'span' }}
                        headingSubTitleProps={{ tag: 'span' }}
                      >
                        <FilterCategoriesLinks
                          domain={ENUM_FILTER.domain.ARTIST}
                          medium={get(artistData, 'mediums[0]')}
                          style={get(artistData, 'styles[0]')}
                        />
                      </SectionContent>
                    </Wrapper>
                  </Section>
                </ScrollMarkerProvider>
              </div>
            </Fragment>
          );
        }}
      </ArtistArtList>
    </LayoutDefault>
  );
};

/**
 * ArtistProfile
 *
 * @param {Props} props
 * @returns {Node}
 */
export default function ArtistProfile(props: Props): Node {
  return (
    <MediaQuery minWidth={raScreenSm}>
      {(isLargeScreen) => <ArtistProfileComponent {...props} isLargeScreen={isLargeScreen} />}
    </MediaQuery>
  );
}
